import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Crown, Briefcase, Headset, Truck, Package, Cog, Wrench, ShieldCheck, Network, ArrowRight,
} from "lucide-react";

const OPS_DIRECTOR_IMG = "https://customer-assets.emergentagent.com/job_laronta-logistics/artifacts/ytv75ldu_ChatGPT%20Image%20Jul%205%2C%202026%2C%2001_54_08%20AM.png";

const leadership = [
  { role: "Chief Executive Officer", short: "CEO", initials: "LP", desc: "Vision, strategy, and long-term partnerships." },
  { role: "Operations Manager & Director", short: "Ops Director", initials: "OM", image: OPS_DIRECTOR_IMG, desc: "Runs day-to-day dispatch, account oversight, and fleet performance from the command desk." },
];

const operations = [
  { role: "Dispatch Lead", short: "Dispatch", initials: "DS", desc: "Assigns loads, monitors ETAs, coordinates drivers." },
  { role: "Dispatch Support", short: "Dispatch", initials: "DS", desc: "Backup dispatcher for overflow & after-hours." },
];

const drivers = [
  { role: "Senior Driver", initials: "D1" },
  { role: "Class A Driver", initials: "D2" },
  { role: "Regional Driver", initials: "D3" },
  { role: "Local Driver", initials: "D4" },
];

const departments = [
  { icon: Package, name: "Delivery", desc: "Last-mile and freight delivery execution — pickup to signed proof of delivery.", members: 6 },
  { icon: Cog, name: "Operational", desc: "Route planning, dispatch, load matching, and customer coordination.", members: 4 },
  { icon: Wrench, name: "Maintenance", desc: "Fleet inspections, DOT compliance, repairs, and preventive service.", members: 3 },
  { icon: ShieldCheck, name: "Safety", desc: "Driver training, log audits, incident response, and regulatory compliance.", members: 2 },
  { icon: Network, name: "Carrier", desc: "Partner-carrier network for expedited & long-haul capacity nationwide.", members: 5 },
];

const stagger = { animate: { transition: { staggerChildren: 0.06 } } };
const rise = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0, transition: { duration: 0.5 } } };

export default function Team() {
  return (
    <div data-testid="team-page" className="min-h-screen pt-24 pb-24 bg-brand-bg">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="text-xs tracking-[0.3em] uppercase font-bold text-primary-brand mb-3">Our Team</div>
          <h1 className="font-heading text-4xl md:text-6xl font-black tracking-tighter">
            People behind every mile.
          </h1>
          <p className="mt-4 text-slate-600 max-w-2xl">
            Leadership, dispatchers, drivers, and specialists — a lean and focused
            crew that keeps your freight moving safely and on time.
          </p>
        </motion.div>

        {/* Leadership */}
        <SectionHeading label="Leadership" title="At the helm." />
        <motion.div variants={stagger} initial="initial" whileInView="animate" viewport={{ once: true }} className="grid grid-cols-1 md:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
          {leadership.map((p, i) => (
            <PersonCard key={p.role} person={p} icon={i === 0 ? Crown : Briefcase} testid={`team-leader-${i}`} />
          ))}
        </motion.div>

        {/* Dispatch */}
        <SectionHeading label="Dispatch" title="Coordinating every load." />
        <motion.div variants={stagger} initial="initial" whileInView="animate" viewport={{ once: true }} className="grid grid-cols-1 md:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
          {operations.map((p, i) => (
            <PersonCard key={p.role} person={p} icon={Headset} testid={`team-dispatch-${i}`} />
          ))}
        </motion.div>

        {/* Drivers */}
        <SectionHeading label="Drivers" title="Behind the wheel." />
        <motion.div variants={stagger} initial="initial" whileInView="animate" viewport={{ once: true }} className="grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
          {drivers.map((d, i) => (
            <motion.div
              key={d.role}
              variants={rise}
              data-testid={`team-driver-${i}`}
              className="bg-brand-bg p-6 hover:bg-white transition-colors"
            >
              <div className="h-16 w-16 flex items-center justify-center bg-white border border-slate-200">
                <Truck className="h-6 w-6 text-primary-brand" />
              </div>
              <div className="mt-5 font-heading font-bold text-slate-900">{d.role}</div>
              <div className="text-xs text-slate-500 uppercase tracking-[0.2em] mt-1">CDL Certified</div>
              <div className="mt-4 h-px w-8 bg-primary-brand" />
            </motion.div>
          ))}
        </motion.div>

        {/* Departments */}
        <SectionHeading label="Departments" title="How we're organized." />
        <motion.div variants={stagger} initial="initial" whileInView="animate" viewport={{ once: true }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
          {departments.map((d, i) => (
            <motion.div
              key={d.name}
              variants={rise}
              data-testid={`team-dept-${d.name.toLowerCase()}`}
              className="group bg-brand-bg p-8 lg:p-10 hover:bg-white transition-colors"
            >
              <div className="flex items-center justify-between">
                <d.icon className="h-8 w-8 text-primary-brand" strokeWidth={1.5} />
                <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">
                  {d.members} members
                </span>
              </div>
              <h3 className="font-heading text-2xl font-bold mt-6">{d.name} Department</h3>
              <p className="text-sm text-slate-600 leading-relaxed mt-3">{d.desc}</p>
              <div className="mt-6 h-px w-8 bg-primary-brand transition-all group-hover:w-16" />
            </motion.div>
          ))}
        </motion.div>

        {/* CTA */}
        <div className="mt-16 border border-slate-200 bg-white p-8 lg:p-12 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase font-bold text-primary-brand mb-2">Careers</div>
            <h3 className="font-heading text-2xl md:text-3xl font-black">Join the crew.</h3>
            <p className="text-slate-600 mt-2 text-sm">
              Class-A drivers, dispatchers, and mechanics — reach out if you're
              ready to run with a professional operation.
            </p>
          </div>
          <Link
            to="/contact"
            data-testid="team-careers-cta"
            className="bg-primary-brand hover:bg-[#E04F00] text-white px-8 py-4 font-bold uppercase tracking-wider text-sm inline-flex items-center gap-2"
          >
            Contact Us <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}

function SectionHeading({ label, title }) {
  return (
    <div className="mt-20 mb-6">
      <div className="text-xs tracking-[0.3em] uppercase font-bold text-primary-brand mb-2">{label}</div>
      <h2 className="font-heading text-3xl md:text-4xl font-black tracking-tight">{title}</h2>
    </div>
  );
}

function PersonCard({ person, icon: Icon, testid }) {
  return (
    <motion.div
      variants={rise}
      data-testid={testid}
      className="bg-brand-bg p-8 flex items-center gap-6 hover:bg-white transition-colors"
    >
      {person.image ? (
        <img
          src={person.image}
          alt={person.role}
          className="h-20 w-20 flex-none object-cover border border-slate-200 rounded-sm"
          loading="lazy"
        />
      ) : (
        <div className="h-20 w-20 flex-none flex items-center justify-center bg-white border border-slate-200 font-heading font-black text-primary-brand text-xl">
          {person.initials}
        </div>
      )}
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-primary-brand" />
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">{person.short}</div>
        </div>
        <div className="font-heading font-bold text-slate-900 text-lg mt-1 truncate">{person.role}</div>
        <div className="text-sm text-slate-600 mt-2">{person.desc}</div>
      </div>
    </motion.div>
  );
}
